///
/// EarthBrowser
///
/// Created by Matt Giger on 10/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Geometry and projection helpers shared by the sky nodes (stars, Milky
/// Way, sun, moon). Everything in the sky is drawn with the depth test off
/// before the globe, through a far projection that reaches the moon and
/// beyond, so the sky's bounds are simply "everything" for culling purposes.
///

import { Perspective, cCameraFOV } from "@stovokor/camera/Perspective"
import { type RenderState } from "@stovokor/engine/RenderState"
import { trig } from "@stovokor/math/math"
import { cube } from "@stovokor/math/cube"
import { type matrix } from "@stovokor/math/matrix"
import { sphere } from "@stovokor/math/sphere"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

// Far plane past the sun (1 AU is 1.5e8 km); near plane well inside the
// camera's minimum distance to any sky body
const cSkyNear = 1000
const cSkyFar = 1e9

// The sun and moon are drawn this many times their true angular size (half
// a degree, ~13px at the default field of view). Both share the factor so
// their relative sizes, and eclipses, stay right
export const cApparentSizeScale = 2.5

/**
 * NodeBase.renderOrder for the sky nodes: negative so they draw before
 * every scene node in their pass regardless of depth (they disable the
 * depth test), lowest first
 */
export const cSkyOrderMilkyWay = -4
export const cSkyOrderStars = -3
export const cSkyOrderMoon = -2
export const cSkyOrderSun = -1

const skyProjection = new Perspective(cCameraFOV, cSkyNear, cSkyFar)

/**
 * View-projection for sky bodies at their true (or at least very large)
 * distances, sharing the scene camera's pose and field of view
 */
export function skyViewProjection(state: RenderState): matrix {
	const cameraProjection = state.camera.projection
	const perspective = cameraProjection instanceof Perspective ? cameraProjection : undefined
	skyProjection.fov = perspective?.fov ?? cCameraFOV
	// Follow the scene camera's lens shift (data dock insets) so the sky
	// stays registered behind the globe
	skyProjection.offsetX = perspective?.offsetX ?? 0
	skyProjection.offsetY = perspective?.offsetY ?? 0
	skyProjection.setSize(state.width, state.height)
	return state.camera.state.lookToward.multiply(skyProjection.projection)
}

/**
 * Logical pixels per radian of view angle at the screen center, for sizing
 * billboards by their angular diameter
 */
export function pixelsPerRadian(state: RenderState): number {
	const cameraProjection = state.camera.projection
	const fov = cameraProjection instanceof Perspective ? cameraProjection.fov : cCameraFOV
	return state.height / (2 * Math.tan(fov * 0.5 * trig.rad))
}

/**
 * A single camera-facing quad: four vertices carrying only their corner
 * in [-1, 1]. The vertex shader places it at a world position and expands
 * it in clip space to a pixel radius (see sun.glsl / moon.glsl)
 */
export class BillboardQuad implements Geometry {
	public mode: DrawMode = DrawMode.Triangles
	public corner: VertexAttribute
	public vertexBuffer: VertexBuffer
	public indexBuffer: IndexBuffer
	public bounds = new cube(-cSkyFar, cSkyFar)
	public boundingSphere = new sphere(cSkyFar)
	public available: boolean = true

	constructor(context: StovokorContext) {
		this.corner = new VertexAttribute(context, 2)
		this.vertexBuffer = new VertexBuffer(context, [this.corner])
		this.vertexBuffer.uploadData(new Float32Array([-1, -1, 1, -1, 1, 1, -1, 1]))
		this.indexBuffer = new IndexBuffer(context, new Uint32Array([0, 1, 2, 0, 2, 3]))
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}

/**
 * One triangle covering the whole viewport in clip space, for shaders that
 * compute a view ray per fragment (the Milky Way background)
 */
export class FullscreenTriangle implements Geometry {
	public mode: DrawMode = DrawMode.Triangles
	public position: VertexAttribute
	public vertexBuffer: VertexBuffer
	public bounds = new cube(-cSkyFar, cSkyFar)
	public boundingSphere = new sphere(cSkyFar)
	public available: boolean = true

	constructor(context: StovokorContext) {
		this.position = new VertexAttribute(context, 2)
		this.vertexBuffer = new VertexBuffer(context, [this.position])
		this.vertexBuffer.uploadData(new Float32Array([-1, -1, 3, -1, -1, 3]))
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
	}
}
